/* eslint-disable react-refresh/only-export-components -- Utility module exports render helpers, not React components. */
import {
  FilterFilled,
  CaretUpOutlined,
  CaretDownOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import { Link } from "react-router";
import { textColor, pillColor, tooltipTitle } from "./VAStatusMapping";
import StatusPill from "../../components/UI/StatusPill";
import { toTitleCase } from "./ActivitiesTableUtilFunctions";

const { Paragraph } = Typography;

export const getFormattedTitles = (
  header: string,
  columnsToFormat: Record<string, string>,
): string => {
  if (!header) return "";
  if (!columnsToFormat) columnsToFormat = {};

  const newTitle = toTitleCase(header);
  if (columnsToFormat[header]) return columnsToFormat[header];
  return newTitle;
};

export const getCellRenderer = (text: any) => {
  // Handle arrays (like therapy, isr_symptoms, etc.)
  if (Array.isArray(text)) {
    return text.join(", ");
  }

  return (
    <StatusPill
      text={text}
      textColor={textColor}
      pillColor={pillColor}
      tooltipTitle={tooltipTitle}
    />
  );
};

export const linkRenderer = (buildTo: (id: string) => string) => {
  function LinkedCell(text: string): React.ReactNode {
    const ids = text.split(", ");
    if (ids.length === 1) {
      return (
        <Link className="text-[#06709B]" to={buildTo(text)} relative="path">
          {text}
        </Link>
      );
    }
    return (
      <span className="flex flex-wrap gap-1">
        {ids.map((id, i, arr) => (
          <span key={id}>
            <Link className="text-[#06709B]" to={buildTo(id)} relative="path">
              {id}
            </Link>
            {i < arr.length - 1 ? "," : null}
          </span>
        ))}
      </span>
    );
  }
  return LinkedCell;
};

export const renderCell = (
  text: unknown,
  header?: string | ((text: string) => React.ReactNode),
) => {
  if (text === null || text === undefined || text === "") return "-";

  if (typeof text === "boolean") return text ? "Yes" : "No";

  if (typeof header === "function" && typeof text === "string") {
    return header(text);
  }

  if (header === "study_status") {
    return (
      <StatusPill
        text={text as any}
        textColor={textColor}
        pillColor={pillColor}
        tooltipTitle={tooltipTitle}
      />
    );
  }

  const displayText = Array.isArray(text) ? text.join(", ") : String(text);

  return (
    <Paragraph
      ellipsis={{ rows: 3, tooltip: displayText }}
      style={{ margin: 0 }}
    >
      {displayText}
    </Paragraph>
  );
};

export const getHiddenColumns = (header: any, columnsToHide: any) => {
  return columnsToHide.has(header);
};

export const getFixedColumns = (header: any, columnsToFix: any) => {
  return columnsToFix.has(header) ? "left" : null;
};

export const getCellWidth = (
  header: string,
  columnWidthConfig: Record<string, number> | null | undefined,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  title?: string,
): number => {
  if (!columnWidthConfig) columnWidthConfig = { DEFAULT: 130 };

  if (columnWidthConfig[header]) {
    return columnWidthConfig[header];
  }

  return Math.max(header.length * 10, columnWidthConfig?.DEFAULT || 130);
};

export const getSearchableColumns = (header: any, columnsToSearch: any) => {
  return columnsToSearch.has(header);
};

export const SORT_HEADERS = {
  SUBJECT: "subject",
  DATE_PATTERNS: ["_date"],
} as const;

export const compareSubjects = (subjectA: any, subjectB: any) => {
  if (!subjectA && !subjectB) return 0;
  if (!subjectA) return 1;
  if (!subjectB) return -1;

  const trimmedA = typeof subjectA === "string" ? subjectA.trim() : subjectA;
  const trimmedB = typeof subjectB === "string" ? subjectB.trim() : subjectB;

  const parseSubject = (subject: any) => {
    return subject.split("-").map((part: any) => parseInt(part, 10) || 0);
  };

  const partsA = parseSubject(trimmedA);
  const partsB = parseSubject(trimmedB);

  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const valueA = partsA[i] || 0;
    const valueB = partsB[i] || 0;

    if (valueA < valueB) return -1;
    if (valueA > valueB) return 1;
  }

  return 0;
};

const parseDateWithUNK = (dateStr: string) => {
  if (!dateStr) return null;

  // Handle YYYY-MM-UNK and YYYY-UNK-UNK formats
  const unkPattern = /^(\d{4})-(UNK|\d{2})-(UNK|\d{2})$/;
  const match = dateStr.match(unkPattern);

  if (match) {
    const [, year, month, day] = match;
    // For UNK values, use a date that sorts appropriately
    // UNK month becomes January (01), UNK day becomes 1st
    const sortableMonth = month === "UNK" ? "01" : month;
    const sortableDay = day === "UNK" ? "01" : day;
    return new Date(`${year}-${sortableMonth}-${sortableDay}`);
  }

  // Try regular date parsing
  return new Date(dateStr);
};

export const compareDates = (dateA: any, dateB: any) => {
  const parsedDateA = parseDateWithUNK(dateA);
  const parsedDateB = parseDateWithUNK(dateB);

  if (parsedDateA === null || parsedDateB === null) return 0;
  if (parsedDateA < parsedDateB) return -1;
  if (parsedDateA > parsedDateB) return 1;
  return 0;
};

const compareGeneric = (aValue: any, bValue: any) => {
  if (!aValue && !bValue) return 0;
  if (!aValue) return 1;
  if (!bValue) return -1;

  // Trim string values before comparison
  const trimmedA = typeof aValue === "string" ? aValue.trim() : aValue;
  const trimmedB = typeof bValue === "string" ? bValue.trim() : bValue;

  const aIsNumeric = !isNaN(parseFloat(trimmedA)) && isFinite(trimmedA);
  const bIsNumeric = !isNaN(parseFloat(trimmedB)) && isFinite(trimmedB);

  if (aIsNumeric && bIsNumeric) {
    return parseFloat(trimmedA) - parseFloat(trimmedB);
  }

  if (typeof trimmedA === "string" && typeof trimmedB === "string") {
    return trimmedA.localeCompare(trimmedB);
  }

  return aIsNumeric ? -1 : bIsNumeric ? 1 : 0;
};

export const getSorter = (header: any) => {
  return (a: any, b: any) => {
    if (header === SORT_HEADERS.SUBJECT) {
      return compareSubjects(a[header], b[header]);
    }

    const isDateField = SORT_HEADERS.DATE_PATTERNS.some((pattern) =>
      header.includes(pattern),
    );

    if (isDateField) {
      return compareDates(a[header], b[header]);
    }

    return compareGeneric(a[header], b[header]);
  };
};

export const tableFilter = (value: any, record: any, dataIndex: any) => {
  const recordValue = record[dataIndex];

  // Handle null/undefined values - they display as "-" but store as null
  if (recordValue === null || recordValue === undefined || recordValue === "") {
    return value === "-";
  }

  if (!value) return false;

  // Handle array fields - check if the filter value is contained in the array
  if (Array.isArray(recordValue)) {
    if (value === "-") {
      return recordValue.length === 0;
    }
    return recordValue.some((item) => {
      const trimmedItem = typeof item === "string" ? item.trim() : item;
      const trimmedFilterValue =
        typeof value === "string" ? value.trim() : value;
      return (
        trimmedItem === trimmedFilterValue ||
        String(trimmedItem) === String(trimmedFilterValue)
      );
    });
  }

  const trimmedRecordValue =
    typeof recordValue === "string" ? recordValue.trim() : recordValue;
  const trimmedFilterValue = typeof value === "string" ? value.trim() : value;

  if (trimmedRecordValue === trimmedFilterValue) return true;

  const recordStr = String(trimmedRecordValue);
  const filterStr = String(trimmedFilterValue);
  if (recordStr === filterStr) return true;

  return false;
};

export const createFilters = (rows: any, header: any) => {
  // Check if this column contains arrays by examining the first non-null value
  const firstNonNullValue = rows.find(
    (row: any) =>
      row[header] !== null && row[header] !== undefined && row[header] !== "",
  )?.[header];

  const isArrayField = Array.isArray(firstNonNullValue);

  const columnValues = new Set();

  if (isArrayField) {
    rows.forEach((row: any) => {
      const value = row[header];
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        !Array.isArray(value)
      ) {
        columnValues.add("-");
      } else {
        value.forEach((item) => {
          const trimmedItem = typeof item === "string" ? item.trim() : item;
          columnValues.add(trimmedItem);
        });
      }
    });
  } else {
    rows.forEach((row: any) => {
      const value = row[header];

      if (value === null || value === undefined || value === "") {
        columnValues.add("-");
      } else {
        const trimmedValue = typeof value === "string" ? value.trim() : value;
        columnValues.add(trimmedValue);
      }
    });
  }

  const filters = Array.from(columnValues)
    .map((value) => {
      return { text: value, value };
    })
    .sort((a, b) => compareGeneric(a.text, b.text));

  return filters;
};

export const CustomFilterIcon = ({ isUnscheduled }: any) => (
  <FilterFilled
    className={`custom-filter-icon ${isUnscheduled ? "unscheduled" : ""}`}
  />
);

export const CustomSortIcon = ({ isUnscheduled, order }: any) => (
  <span
    className={`inline-flex flex-col items-center custom-sort-icon ${
      isUnscheduled ? "unscheduled" : ""
    }`}
  >
    <CaretUpOutlined
      className={`text-[12px] sort-up ${order === "ascend" ? "active" : ""}`}
    />
    <CaretDownOutlined
      className={`text-[12px] mt-[-0.3em] sort-down ${
        order === "descend" ? "active" : ""
      }`}
    />
  </span>
);

const searchableFilter = ["subject", "site"];

export const sorterFunction = (variable: string) => (a: any, b: any) => {
  if (
    (!isNaN(Number(a[variable])) && !isNaN(Number(b[variable]))) ||
    (typeof a[variable] !== "string" && typeof b[variable] !== "string")
  ) {
    return Number(a[variable]) - Number(b[variable]);
  }
  if (!isNaN(Number(a[variable])) && isNaN(Number(b[variable]))) return 1;
  if (!isNaN(Number(b[variable])) && isNaN(Number(a[variable]))) return -1;

  if (typeof a[variable] === "string" && typeof b[variable] === "string") {
    return "" + a[variable].localeCompare("" + b[variable]);
  }
};

export const filterFunction = (data: any, variable: string) => {
  if (data.length === 0) return {};
  if (data && !data[0]?.children) {
    const isArray = Array.isArray(data[0][variable]);

    return {
      filterSearch: searchableFilter.includes(variable),
      filters: isArray
        ? Array.from(new Set(data?.flatMap((d: any) => d[variable])))
            .sort((a, b) => `${a}`.localeCompare(`${b}`))
            .map((datum) => {
              return { text: datum, value: datum };
            })
        : Array.from(new Set(data?.map((d: any) => d[variable])))
            .sort((a, b) => `${a}`.localeCompare(`${b}`))
            .map((datum) => {
              return { text: datum, value: datum };
            }),

      onFilter: (value: string, record: any) => {
        if (isArray) {
          return record[variable].includes(value);
        }
        return ("" + record[variable]).indexOf(value) === 0;
      },
    };
  }
};

// Normalize text for robust mapping lookups
export const normalizeText = (text: any) => {
  if (!text || typeof text !== "string") return text;
  return text
    .trim() // Remove leading/trailing whitespace
    .replace(/[\u2013\u2014]/g, "-") // Replace en dash (–) and em dash (—) with hyphen (-)
    .replace(/[\u2018\u2019]/g, "'") // Replace curly single quotes with straight quotes
    .replace(/[\u201C\u201D]/g, '"') // Replace curly double quotes with straight quotes
    .replace(/\s+/g, " "); // Replace multiple spaces with single space
};

export const getMappingValue = (mapping: any, key: any) => {
  if (mapping[key]) {
    return mapping[key];
  }

  // Try normalized match
  const normalizedKey = normalizeText(key);
  const normalizedMapping = Object.fromEntries(
    Object.entries(mapping).map(([k, v]) => [normalizeText(k), v]),
  );

  return normalizedMapping[normalizedKey];
};
